/**
 * Static vocabulary for the Rate Shop UI (MCV2 → Orders → Rate Shop).
 *
 * Mirrors merlin-core `integrations/shipStation/v2/rateShop/{carrierConfig,configStore,rulesEngine}.js`
 * and docs/RATE-SHOP-RULES-UI-PRD.md (hand-off 2026-11-04). The backend ignores unknown
 * fields / operators / action types silently, so everything the editor offers MUST come
 * from these lists.
 */
import carrierCatalog from './carrierCatalog.json';
import { FIELD_COPY, OPERATOR_WORDS, ACTION_COPY } from './ruleEditorCopy';

export const SETTINGS_COLLECTION = 'rateShopSettings';
export const SETTINGS_DOC = 'active';
export const RULES_COLLECTION = 'rateShopRules';
export const DECISIONS_COLLECTION = 'rateShopDecisions';
export const RUNS_COLLECTION = 'rateShopRuns';
export const SHIPSTATION_TAGS_COLLECTION = 'shipStationTags';

/** v2 store id = "se-" + V1 storeId. */
export const STORES = [
  { id: 'se-473849', label: 'Amazon US' },
  { id: 'se-473848', label: 'eBay' },
  { id: 'se-473569', label: 'Walmart US' },
  { id: 'se-474015', label: 'Shopify STK' },
  { id: 'se-523157', label: 'Amazon Start Smart' },
  { id: 'se-516034', label: 'Amazon Tee Tender' }
];
export const STORE_LABELS = Object.fromEntries(STORES.map(s => [s.id, s.label]));
export const storeLabel = id => STORE_LABELS[id] || id || '—';

export const GROUPS = [
  { id: 'CORE', label: 'Standard package rates', help: 'Asks USPS, UPS, FedEx and Amazon Shipping for their normal package prices.' },
  { id: 'MI', label: 'UPS Mail Innovations', help: 'Very cheap for light domestic parcels, slow, no delivery estimate.' },
  { id: 'WALMART', label: 'Ship with Walmart', help: 'Walmart Marketplace orders only.' },
  { id: 'AMAZON', label: 'Amazon Buy Shipping', help: 'Amazon orders only; off by default.' }
];
export const GROUP_IDS = GROUPS.map(g => g.id);

/** Agreed 2026-11-04 — cheapest of these wins regardless of transit time. */
export const DEFAULT_COMPARABLE_SERVICES = Object.freeze([
  'usps_ground_advantage', 'usps_priority_mail',
  'amazon_shipping_standard',
  'fedex_home_delivery', 'fedex_ground', 'fedex_smartpost_parcel_select', 'fedex_ground_economy_parcel_select',
  'ups_ground', 'wwex_ups_ground',
  'ups_surepost_less_than_1_lb', 'ups_surepost_1_lb_or_greater', 'ups_ground_saver', 'wwex_ups_ground_saver',
  'expedited_mail_innovations',
  'ship_with_walmart_fedex_us_ground', 'ship_with_walmart_fedex_home_delivery',
  'ship_with_walmart_fedex_ground_economy', 'ship_with_walmart_usps_priority_mail',
  'ship_with_walmart_usps_ground_advantage'
]);

export const DEFAULT_SETTINGS = Object.freeze({
  enabled: false,
  dryRun: true,
  storesInScope: ['se-473849', 'se-473848', 'se-473569'],
  groups: { CORE: true, MI: true, WALMART: true, AMAZON: false },
  comparableServices: [...DEFAULT_COMPARABLE_SERVICES],
  carrierAccounts: {},
  tags: {
    done: 'RateShop',
    manual: 'RateShop Manual',
    needsDims: 'RateShop Needs Dims',
    error: 'RateShop Error'
  },
  sweep: { lookbackHours: 72, maxShipmentsPerRun: 150, pageSize: 100 },
  respectHumanOverride: true
});

/** Exact built-in fallback rules from merlin-core configStore.js (2026-11-04). */
export const SYSTEM_RULES = Object.freeze([
  {
    id: 'sys-skip-hazmat',
    name: 'Skip hazmat / dry ice / aerosol',
    enabled: true,
    priority: 10,
    stopProcessing: true,
    system: true,
    match: {
      any: [
        {
          field: 'tag',
          op: 'has',
          value: ['Explosive', 'FROZEN/DRY ICE', 'Aerosol']
        }
      ]
    },
    actions: [
      { type: 'skip', reason: 'excluded tag (hazmat / dry ice / aerosol)' }
    ],
    notes:
      'Agreed 2026-11-04: never rate-shop these; the warehouse handles them by hand.'
  },
  {
    id: 'sys-skip-international',
    name: 'Skip international',
    enabled: true,
    priority: 11,
    stopProcessing: true,
    system: true,
    match: {
      any: [
        { field: 'countryCode', op: 'neq', value: 'US' },
        { field: 'tag', op: 'has', value: 'International' }
      ]
    },
    actions: [{ type: 'skip', reason: 'international shipment' }],
    notes:
      'MI and Ship with Walmart are domestic-only; international stays on the in-app rules.'
  },
  {
    id: 'sys-skip-manual',
    name: 'Skip when tagged RateShop Manual',
    enabled: true,
    priority: 12,
    stopProcessing: true,
    system: true,
    match: { all: [{ field: 'tag', op: 'has', value: 'RateShop Manual' }] },
    actions: [{ type: 'skip', reason: 'manual override tag' }]
  },
  {
    id: 'sys-skip-do-not-ship',
    name: 'Skip DO NOT SHIP / RTS / Needs Review',
    enabled: true,
    priority: 13,
    stopProcessing: true,
    system: true,
    match: {
      any: [
        {
          field: 'tag',
          op: 'has',
          value: ['DO NOT SHIP', 'RTS', 'Needs Review']
        }
      ]
    },
    actions: [{ type: 'skip', reason: 'held order tag' }]
  }
]);

/** Decision statuses written by rateShopCron.js. */
export const DECISION_STATUSES = [
  { id: 'dry-run', label: 'Dry run', variant: 'info' },
  { id: 'applied', label: 'Applied', variant: 'success' },
  { id: 'already-optimal', label: 'Already optimal', variant: 'secondary' },
  { id: 'skipped', label: 'Skipped', variant: 'warning' },
  { id: 'no-rates', label: 'No rates', variant: 'danger' },
  { id: 'human-override', label: 'Human override', variant: 'dark' },
  { id: 'reverted', label: 'Reverted', variant: 'warning' },
  { id: 'error', label: 'Error', variant: 'danger' }
];
export const statusMeta = id => DECISION_STATUSES.find(s => s.id === id) || { id, label: id || '—', variant: 'light' };

/**
 * Condition fields. `type` drives the operator list and value input:
 *   string → text; number → number; bool → toggle; list → multi-value (tag picker / sku chips)
 */
export const CONDITION_FIELDS = [
  { id: 'storeId', label: 'Store', type: 'string', input: 'store' },
  { id: 'countryCode', label: 'Ship-to country', type: 'string', placeholder: 'US' },
  { id: 'state', label: 'Ship-to state', type: 'string', placeholder: 'OH' },
  { id: 'postalPrefix', label: 'Ship-to postal code', type: 'string', placeholder: '452', help: 'use "starts with" for a prefix' },
  { id: 'residential', label: 'Residential address', type: 'bool' },
  { id: 'weightOz', label: 'Weight (oz)', type: 'number' },
  { id: 'longestSideIn', label: 'Longest side (in)', type: 'number' },
  { id: 'itemCount', label: 'Item count', type: 'number' },
  { id: 'orderTotal', label: 'Order total ($)', type: 'number' },
  { id: 'tag', label: 'ShipStation tag', type: 'list', input: 'tag' },
  { id: 'sku', label: 'SKU', type: 'list' },
  { id: 'externalOrderId', label: 'Marketplace order id', type: 'string' }
].map(field => ({ ...field, ...FIELD_COPY[field.id] }));
export const CONDITION_FIELD_IDS = CONDITION_FIELDS.map(f => f.id);
export const conditionField = id => CONDITION_FIELDS.find(f => f.id === id);

/** Operators. `value` describes the expected value shape for validation + inputs. */
export const OPERATORS = [
  { id: 'eq', label: 'equals', types: ['string', 'number', 'bool', 'list'], value: 'single' },
  { id: 'neq', label: 'does not equal', types: ['string', 'number', 'bool', 'list'], value: 'single' },
  { id: 'in', label: 'is one of', types: ['string', 'number', 'list'], value: 'many' },
  { id: 'not_in', label: 'is not one of', types: ['string', 'number', 'list'], value: 'many' },
  { id: 'gt', label: '>', types: ['number'], value: 'single' },
  { id: 'gte', label: '≥', types: ['number'], value: 'single' },
  { id: 'lt', label: '<', types: ['number'], value: 'single' },
  { id: 'lte', label: '≤', types: ['number'], value: 'single' },
  { id: 'between', label: 'is between', types: ['number'], value: 'range' },
  { id: 'contains', label: 'contains', types: ['string', 'list'], value: 'single' },
  { id: 'starts_with', label: 'starts with', types: ['string', 'list'], value: 'single' },
  { id: 'has', label: 'has any of', types: ['list'], value: 'many' },
  { id: 'not_has', label: 'has none of', types: ['list'], value: 'many' }
].map(op => ({ ...op, wordLabel: OPERATOR_WORDS[op.id] }));
export const OPERATOR_IDS = OPERATORS.map(o => o.id);
export const operator = id => OPERATORS.find(o => o.id === id);
export const operatorsForField = (fieldId, currentOp) => {
  const f = conditionField(fieldId);
  const allowed = {
    tag: ['has', 'not_has'],
    sku: ['has', 'not_has', 'starts_with'],
    storeId: ['eq', 'neq', 'in', 'not_in']
  }[fieldId] || (f && { number: ['gt', 'gte', 'lt', 'lte', 'between', 'eq'], bool: ['eq'] }[f.type]);
  return f ? OPERATORS.filter(o => o.id === currentOp ||
    (o.types.includes(f.type) && (!allowed || allowed.includes(o.id)))) : [];
};

/**
 * Actions. `params` lists the keys the editor must collect; `input` hints the control.
 */
export const ACTION_TYPES = [
  { id: 'skip', label: 'Skip this order', params: [{ key: 'reason', label: 'Reason', input: 'text', required: false }] },
  { id: 'exclude_services', label: 'Exclude services', params: [{ key: 'services', label: 'Services', input: 'services', required: true }] },
  { id: 'only_services', label: 'Only these services', params: [{ key: 'services', label: 'Services', input: 'services', required: true }] },
  { id: 'exclude_carriers', label: 'Exclude carrier accounts', params: [{ key: 'carriers', label: 'Carrier accounts', input: 'carriers', required: true }] },
  { id: 'only_carriers', label: 'Only these carrier accounts', params: [{ key: 'carriers', label: 'Carrier accounts', input: 'carriers', required: true }] },
  { id: 'exclude_groups', label: 'Do not rate groups', params: [{ key: 'groups', label: 'Rating methods', input: 'groups', required: true }] },
  {
    id: 'force_service', label: 'Force a service',
    params: [
      { key: 'serviceCode', label: 'Service', input: 'service', required: true },
      { key: 'carrierId', label: 'Carrier account (optional)', input: 'carrier', required: false },
      { key: 'packageCode', label: 'Package code (optional)', input: 'package', required: false }
    ]
  },
  {
    id: 'prefer_service', label: 'Prefer a service within tolerance',
    params: [
      { key: 'serviceCode', label: 'Service', input: 'service', required: true },
      { key: 'carrierId', label: 'Carrier account (optional)', input: 'carrier', required: false },
      { key: 'withinAmount', label: 'Within $', input: 'number', required: false },
      { key: 'withinPercent', label: 'Within %', input: 'number', required: false }
    ]
  },
  { id: 'add_tag', label: 'Add ShipStation tag on write-back', params: [{ key: 'tag', label: 'Tag', input: 'tag', required: true }] }
].map(action => ({ ...action, ...ACTION_COPY[action.id] }));
export const ACTION_TYPE_IDS = ACTION_TYPES.map(a => a.id);
export const actionType = id => ACTION_TYPES.find(a => a.id === id);

/** ---- Carrier / service / package catalogue (static dump of discoverCarriers.js, 2026-11-04) ---- */
export const CARRIERS = carrierCatalog.carriers;
export const RATE_SHOP_CARRIER_IDS = ['se-287055', 'se-443513', 'se-730609', 'se-396926', 'se-305064', 'se-996991', 'se-1142304', 'se-979073', 'se-979074', 'se-1141922'];
export const CATALOG_DISCOVERED_AT = carrierCatalog.discoveredAt;
export const carrierById = id => CARRIERS.find(c => c.carrierId === id);
export const carrierLabel = id => {
  const c = carrierById(id);
  return c ? `${c.name} (${c.carrierCode}, ${c.carrierId})` : id || '—';
};

/** Unique services across all carriers: [{ code, name, carrierCodes[] }], sorted by code. */
const catalogServices = Object.values(
  CARRIERS.reduce((acc, c) => {
    c.services.forEach(s => {
      const cur = acc[s.code] || { code: s.code, name: s.name, carrierCodes: [] };
      if (!cur.carrierCodes.includes(c.carrierCode)) cur.carrierCodes.push(c.carrierCode);
      acc[s.code] = cur;
    });
    return acc;
  }, {})
).sort((a, b) => a.code.localeCompare(b.code));
export const SERVICES = [...catalogServices, ...DEFAULT_COMPARABLE_SERVICES
  .filter(code => !catalogServices.some(s => s.code === code))
  .map(code => ({ code, name: code, carrierCodes: [] }))]
  .sort((a, b) => a.code.localeCompare(b.code));
const serviceNames = SERVICES.map(s => s.name.replace(/_/g, ' '));
const pickerLabels = SERVICES.map((s, i) => {
  const name = serviceNames[i];
  if (serviceNames.filter(n => n === name).length === 1) return name;
  const accounts = CARRIERS.filter(c => c.services.some(item => item.code === s.code));
  return `${name} · ${[...new Set(accounts.map(c => c.name))].join(' / ')}`;
});
const serviceLabels = Object.fromEntries(SERVICES.map((s, i) => [s.code,
  pickerLabels.filter(label => label === pickerLabels[i]).length > 1
    ? `${pickerLabels[i]} · ${s.code}` : pickerLabels[i]
]));
export const servicePickerLabel = code => serviceLabels[code] || code || 'a service';
export const serviceByCode = code => SERVICES.find(s => s.code === code);
export const serviceLabel = code => {
  const s = serviceByCode(code);
  return s ? (s.name === s.code ? s.code : `${s.name} (${s.code})`) : code || '—';
};

/** Services grouped by carrier code for the comparable-services checklist. */
export const SERVICES_BY_CARRIER_CODE = SERVICES.reduce((acc, s) => {
  s.carrierCodes.forEach(cc => {
    acc[cc] = acc[cc] || [];
    acc[cc].push(s);
  });
  return acc;
}, {});

/** Unique package codes across carriers. */
export const PACKAGES = Object.values(
  CARRIERS.reduce((acc, c) => {
    c.packages.forEach(p => { acc[p.code] = acc[p.code] || { code: p.code, name: p.name }; });
    return acc;
  }, {})
).sort((a, b) => a.code.localeCompare(b.code));

export const shipStationOrderUrl = externalOrderId =>
  `https://ship.shipstation.com/orders/all-orders-search-result?quickSearch=${encodeURIComponent(externalOrderId || '')}`;
